import { useCallback, useState } from 'react';
import { trpc } from '../lib/trpc';

export interface FileUploadStatus {
  file: File;
  status: 'idle' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  fileId?: number;
}

export function useFileUpload() {
  const [uploadStatuses, setUploadStatuses] = useState<Record<string, FileUploadStatus>>({});

  const getUploadUrls = trpc.files.getUploadUrls.useMutation();
  const registerFile = trpc.files.create.useMutation();

  const uploadFile = useCallback(
    async (file: File, uploadUrl: string, r2Path: string, category?: string): Promise<number> => {
      const key = `${file.name}-${file.size}-${file.lastModified}`;

      setUploadStatuses((prev) => ({
        ...prev,
        [key]: { ...prev[key], file, status: 'uploading', progress: 0 },
      }));

      try {
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
          body: file,
        });

        if (!response.ok) throw new Error(`Upload failed with status ${response.status}`);

        const result = await registerFile.mutateAsync({
          fileName: file.name,
          fileType: file.type || 'application/octet-stream',
          fileSize: file.size,
          r2Path,
          category,
        });

        if (!result.success || !result.file) throw new Error('Failed to register file in database');

        setUploadStatuses((prev) => ({
          ...prev,
          [key]: { ...prev[key], status: 'success', progress: 100, fileId: result.file!.id },
        }));

        return result.file.id;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Unknown upload error';
        setUploadStatuses((prev) => ({
          ...prev,
          [key]: { ...prev[key], status: 'error', error: errorMsg },
        }));
        throw err;
      }
    },
    [registerFile]
  );

  const uploadAll = useCallback(
    async (files: File[], category?: string) => {
      if (files.length === 0) return [];

      const initialStatuses: Record<string, FileUploadStatus> = {};
      files.forEach((f) => {
        initialStatuses[`${f.name}-${f.size}-${f.lastModified}`] = {
          file: f,
          status: 'idle',
          progress: 0,
        };
      });
      setUploadStatuses((prev) => ({ ...prev, ...initialStatuses }));

      const urls = await getUploadUrls.mutateAsync({
        files: files.map((f) => ({
          fileName: f.name,
          fileType: f.type || 'application/octet-stream',
          // Lets the server reject an over-quota batch before any bytes are pushed to R2.
          fileSize: f.size,
        })),
      });

      const uploadPromises = files.map((file, i) =>
        uploadFile(file, urls[i].uploadUrl, urls[i].r2Path, category).catch((err) => {
          console.error(`Failed to upload ${file.name}:`, err);
          return null;
        })
      );

      const results = await Promise.all(uploadPromises);
      return results.filter((id): id is number => id !== null);
    },
    [getUploadUrls, uploadFile]
  );

  return {
    uploadAll,
    uploadStatuses,
    isUploading:
      getUploadUrls.isPending ||
      Object.values(uploadStatuses).some((s) => s.status === 'uploading'),
  };
}
